import rehypeRaw from 'rehype-raw';
import rehypeSanitize, { defaultSchema } from 'rehype-sanitize';
import { BUTTON_DATA_ATTRIBUTES } from './messageHtml';

// The Markdown side of the message HTML policy in messageHtml.js. rehype-raw turns the
// HTML in a message into real elements, so rehype-sanitize has to run after it.
//
// The schema is GitHub's default (no script/iframe/form/style elements, no style or on*
// attributes, safe link protocols) with:
// - the class names the channel's message styles define, next to the classes GFM sets;
// - in-message buttons, with their data-sure-* attributes;
// - the SVG shapes icons in greetings and fragments are drawn with;
// - lazy images, tel: links and link targets;
// - <style> stripped with its contents, so its CSS does not show up as text;
// - `input` removed (the default allows it only as a disabled GFM task-list checkbox).

const SVG_TAG_NAMES = [
  'svg',
  'g',
  'path',
  'circle',
  'ellipse',
  'rect',
  'line',
  'polyline',
  'polygon',
];
const SVG_ATTRIBUTES = [
  'viewBox',
  'width',
  'height',
  'fill',
  'fillOpacity',
  'fillRule',
  'clipRule',
  'stroke',
  'strokeWidth',
  'strokeLineCap',
  'strokeLineJoin',
  'strokeOpacity',
  'opacity',
  'transform',
  'd',
  'cx',
  'cy',
  'r',
  'rx',
  'ry',
  'x',
  'y',
  'x1',
  'y1',
  'x2',
  'y2',
  'points',
];

// data-sure-post -> dataSurePost, the property name hast uses.
const hastProperty = (attribute) =>
  attribute.replace(/-([a-z])/g, (_, letter) => letter.toUpperCase());

const isClassName = (definition) =>
  (Array.isArray(definition) ? definition[0] : definition) === 'className';

// A tag's own className rule replaces the '*' one, so the channel's classes are added to
// each of them too. Without classes nothing is added: a one-item ['className'] rule would
// allow every class.
const withMessageClasses = (definitions = [], classNames) => {
  if (classNames.length === 0) return definitions;
  const own = definitions.find(isClassName);
  return [
    ...definitions.filter((definition) => !isClassName(definition)),
    Array.isArray(own) ? [...own, ...classNames] : ['className', ...classNames],
  ];
};

const { input: _inputAttributes, ...defaultAttributes } = defaultSchema.attributes;
const { input: _inputRequired, ...required } = defaultSchema.required;

// The schema for a channel whose message styles define these class names.
export const messageSanitizeSchema = (classNames = []) => {
  const attributes = Object.fromEntries(
    Object.entries(defaultAttributes).map(([tagName, definitions]) => [
      tagName,
      withMessageClasses(definitions, classNames),
    ]),
  );
  return {
    ...defaultSchema,
    tagNames: [
      ...defaultSchema.tagNames.filter((tagName) => tagName !== 'input'),
      'button',
      ...SVG_TAG_NAMES,
    ],
    attributes: {
      ...attributes,
      a: [...attributes.a, 'target', 'rel'],
      img: [...attributes.img, 'alt', 'width', 'height', ['loading', 'lazy', 'eager']],
      button: [['type', 'button'], ...BUTTON_DATA_ATTRIBUTES.map(hastProperty)],
      ...Object.fromEntries(SVG_TAG_NAMES.map((tagName) => [tagName, SVG_ATTRIBUTES])),
    },
    required,
    protocols: { ...defaultSchema.protocols, href: [...defaultSchema.protocols.href, 'tel'] },
    strip: [...defaultSchema.strip, 'style'],
  };
};

export const messageRehypePlugins = (classNames = []) => [
  rehypeRaw,
  [rehypeSanitize, messageSanitizeSchema(classNames)],
];
